import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { chatApi } from './api.js';
import { useLiveRefresh } from './live.js';
import type { ConversationSummary } from './types.js';

/** Asks the bottom bar and People to count unread messages again. */
export const unreadChanged = () => window.dispatchEvent(new Event('rydres:unread'));

/** When a message was sent: the time today, the date before that. */
export const when = (iso: string) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/** A conversation's last message for a row in People. */
export const lastLine = (c: ConversationSummary) =>
  c.closed ? 'Messages turned off' : c.last ? `${c.last.mine ? 'You: ' : ''}${c.last.body}` : 'No messages yet';

/** Opens a conversation over the current screen (`?chat=<id>`); Back closes it again. */
export function useOpenChat() {
  const [, setParams] = useSearchParams();
  return useCallback(
    (id: string) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.set('chat', id);
          return next;
        },
        { state: { chatOpened: true } },
      ),
    [setParams],
  );
}

/** The driver's conversations, kept fresh while People is on screen. */
export function useConversations() {
  const [list, setList] = useState<ConversationSummary[] | null>(null);
  const load = useCallback(() => {
    chatApi.list().then(
      (r) => setList(r.conversations),
      () => setList((l) => l ?? []),
    );
  }, []);
  useLiveRefresh(load, { on: 'messages-changed', everyMs: 15_000 });
  useEffect(() => {
    window.addEventListener('rydres:unread', load);
    return () => window.removeEventListener('rydres:unread', load);
  }, [load]);
  return list;
}
